/**
 * 「気球を探す」（カメラの映像に受信中の気球の位置を重ねる、docs/08 §234）の計算。
 * DOM を使わない純粋関数だけを置く（テストしやすいように）。
 *
 * 座標の約束（W3C DeviceOrientation と同じ）:
 * - 地上の座標: x=東・y=北・z=上
 * - 端末の座標: x=画面の右・y=画面の上・z=画面から手前（自分の方）。**背面カメラは −z を向く**
 * - 端末の向き（alpha, beta, gamma）から、端末→地上の回転 R = Rz(alpha)·Rx(beta)·Ry(gamma)
 * - 方位は北から時計回り [0,360)、仰角は水平から上が正
 *
 * alpha は端末によって「磁北が基準（Android の deviceorientationabsolute）」と
 * 「起動時の向きが基準（iPhone）」がある。どちらでも、alpha の基準での方位（以下「端末の方位」）と
 * 真方位の差 H を足し引きして扱う: 真方位 = 端末の方位 + H。
 */

const D = Math.PI / 180;
/** 地球の半径 [m] */
const EARTH_R = 6_371_000;
/** 光の屈折で、地球の丸みによる見かけの沈みが減る割合（測量でよく使う 0.13） */
const REFRACTION_K = 0.13;

export type Vec3 = [number, number, number];
/** 3×3の行列（行優先） */
export type Mat3 = [number, number, number, number, number, number, number, number, number];

export interface Orientation {
  alpha: number;
  beta: number;
  gamma: number;
}

export function normDeg(d: number): number {
  return ((d % 360) + 360) % 360;
}

/** a − b を (−180, 180] で */
export function angleDiff(a: number, b: number): number {
  const d = normDeg(a - b);
  return d > 180 ? d - 360 : d;
}

/** 端末→地上の回転行列 */
export function rotationMatrix(o: Orientation): Mat3 {
  const ca = Math.cos(o.alpha * D);
  const sa = Math.sin(o.alpha * D);
  const cb = Math.cos(o.beta * D);
  const sb = Math.sin(o.beta * D);
  const cg = Math.cos(o.gamma * D);
  const sg = Math.sin(o.gamma * D);
  // Rz(a)·Rx(b)·Ry(g)
  return [
    ca * cg - sa * sb * sg, -sa * cb, ca * sg + sa * sb * cg,
    sa * cg + ca * sb * sg, ca * cb, sa * sg - ca * sb * cg,
    -cb * sg, sb, cb * cg,
  ];
}

/** 端末の座標 → 地上の座標 */
export function deviceToWorld(r: Mat3, v: Vec3): Vec3 {
  return [
    r[0] * v[0] + r[1] * v[1] + r[2] * v[2],
    r[3] * v[0] + r[4] * v[1] + r[5] * v[2],
    r[6] * v[0] + r[7] * v[1] + r[8] * v[2],
  ];
}

/** 地上の座標 → 端末の座標（回転行列の転置） */
export function worldToDevice(r: Mat3, v: Vec3): Vec3 {
  return [
    r[0] * v[0] + r[3] * v[1] + r[6] * v[2],
    r[1] * v[0] + r[4] * v[1] + r[7] * v[2],
    r[2] * v[0] + r[5] * v[1] + r[8] * v[2],
  ];
}

export function dirFromAzEl(azDeg: number, elDeg: number): Vec3 {
  const ce = Math.cos(elDeg * D);
  return [ce * Math.sin(azDeg * D), ce * Math.cos(azDeg * D), Math.sin(elDeg * D)];
}

export function azElFromDir(v: Vec3): { az: number; el: number } {
  const h = Math.hypot(v[0], v[1]);
  return { az: normDeg(Math.atan2(v[0], v[1]) / D), el: Math.atan2(v[2], h) / D };
}

/** 背面カメラが向いている方位（端末の方位＝alpha の基準）と仰角 */
export function cameraAzEl(o: Orientation): { az: number; el: number } {
  return azElFromDir(deviceToWorld(rotationMatrix(o), [0, 0, -1]));
}

/**
 * 自分から相手を見たときの方位（真方位）・仰角・水平距離。数十km以内を想定した平面近似に、
 * 地球の丸み（屈折を考慮）による見かけの沈みを入れる（10km で約7m）
 */
export function lookAngles(
  self: { lat: number; lon: number; altM: number },
  target: { lat: number; lon: number; altM: number },
): { azDeg: number; elDeg: number; distM: number } {
  const north = (target.lat - self.lat) * D * EARTH_R;
  const east = (target.lon - self.lon) * D * EARTH_R * Math.cos(((self.lat + target.lat) / 2) * D);
  const distM = Math.hypot(north, east);
  const drop = ((distM * distM) / (2 * EARTH_R)) * (1 - REFRACTION_K);
  const dz = target.altM - self.altM - drop;
  return { azDeg: normDeg(Math.atan2(east, north) / D), elDeg: Math.atan2(dz, Math.max(distM, 1)) / D, distM };
}

export interface FixLike {
  t: number;
  lat: number;
  lon: number;
  altMsl: number | null;
  altPressure?: number | null;
}

/**
 * いまの位置を、最新の点と少し前の点の速さから予測する（送信間隔のあいだに動いたぶん）。
 * 予測は最大 `maxAheadMs` まで（それより古い位置は、動いた先が当てにならないので伸ばさない）。
 * 高度は伸ばさない（上下は変わりやすい）
 */
export function predictPosition(
  fixes: readonly FixLike[],
  nowMs: number,
  maxAheadMs = 120_000,
): { lat: number; lon: number; altM: number | null; ageMs: number; speedMps: number } | null {
  const last = fixes[fixes.length - 1];
  if (!last) return null;
  const ageMs = Math.max(0, nowMs - last.t);
  const altM = last.altPressure ?? last.altMsl;
  // 10秒以上前の点と比べて速さを出す（間隔が短すぎるとGPSのゆらぎで向きが暴れる）
  let prev: FixLike | null = null;
  for (let i = fixes.length - 2; i >= 0; i--) {
    if (last.t - fixes[i].t >= 10_000) {
      prev = fixes[i];
      break;
    }
  }
  if (!prev || last.t - prev.t > 300_000) return { lat: last.lat, lon: last.lon, altM, ageMs, speedMps: 0 };
  const dtS = (last.t - prev.t) / 1000;
  const vLat = (last.lat - prev.lat) / dtS;
  const vLon = (last.lon - prev.lon) / dtS;
  const ahead = Math.min(ageMs, maxAheadMs) / 1000;
  const speedMps = Math.hypot(vLat * D * EARTH_R, vLon * D * EARTH_R * Math.cos(last.lat * D));
  return { lat: last.lat + vLat * ahead, lon: last.lon + vLon * ahead, altM, ageMs, speedMps };
}

/**
 * カメラの映像（`object-fit: cover` で枠に敷いたもの）の焦点距離 [画面px]。
 * `longSideFovDeg` は映像の長辺方向の画角（機種で違うので、利用者が調整できる）
 */
export function focalPx(boxW: number, boxH: number, videoW: number, videoH: number, longSideFovDeg: number): number {
  const longPx = Math.max(videoW, videoH);
  const f = longPx / 2 / Math.tan((longSideFovDeg / 2) * D);
  const scale = Math.max(boxW / videoW, boxH / videoH);
  return f * scale;
}

export interface CameraView {
  width: number;
  height: number;
  focal: number;
}

/**
 * 地上の方向（端末の方位・仰角）を、カメラの映像の上の位置へ。カメラの後ろ側なら `front: false`
 * （画面の外の左右どちらにあるかは `x` の符号で分かる）
 */
export function projectToScreen(
  r: Mat3,
  azDeg: number,
  elDeg: number,
  cam: CameraView,
): { x: number; y: number; front: boolean } {
  const d = worldToDevice(r, dirFromAzEl(azDeg, elDeg));
  const forward = -d[2];
  if (forward <= 1e-3) {
    // 後ろ: 左右どちらへ回せばよいかだけ分かるよう、画面の外の遠くに置く
    return { x: d[0] >= 0 ? cam.width * 10 : -cam.width * 10, y: cam.height / 2, front: false };
  }
  return { x: cam.width / 2 + (cam.focal * d[0]) / forward, y: cam.height / 2 - (cam.focal * d[1]) / forward, front: true };
}

/** 画面の位置から、その方向の端末の方位・仰角（補正のタップに使う） */
export function rayFromScreen(r: Mat3, x: number, y: number, cam: CameraView): { az: number; el: number } {
  const dev: Vec3 = [(x - cam.width / 2) / cam.focal, -(y - cam.height / 2) / cam.focal, -1];
  return azElFromDir(deviceToWorld(r, dev));
}

/** 丸い量（角度）の指数移動平均。`weight` は新しい値の重み */
export function smoothAngle(prev: number | null, next: number, weight: number): number {
  if (prev == null) return normDeg(next);
  return normDeg(prev + angleDiff(next, prev) * weight);
}
